import React from "react";
import {
  Text,
  Tag,
  Link,
  theme,
  Spacer,
  SVG,
  Button,
  Popover,
} from "@local-logic/core/ui";
import { primitiveUtils, typeguardUtils } from "@local-logic/core/utils";
import Walking from "~/assets/icons/Walking.svg";
import Driving from "~/assets/icons/directions_car.svg";
import { poiIcons } from "~/sdks/LocalContent/constants/pois";
import { useRootElement } from "../context";
import * as styles from "./styles";
import * as translations from "./translations";

export const Content = () => {
  const {
    options: {
      poi,
      side,
      collisionPadding,
      type,
      walkingTime,
      drivingTime,
      showDriveTime,
      calculateRoute,
    },
  } = useRootElement();
  const poiType =
    type === "high_schools" || type === "primary_schools" ? "school" : "poi";

  const mapLanguages = (languages: ("en" | "fr" | "es" | string)[]) =>
    languages.map((l) => {
      switch (l) {
        case "en":
          return translations.schoolEnglish;
        case "fr":
          return translations.schoolFrench;
        case "es":
          return translations.schoolSpanish;
        default:
          return l;
      }
    });

  const SchoolText = (key: string, value: string) => (
    <>
      <Text>{key}: </Text>
      <Text color={theme["--ll-color-on-background-variant1"]}>
        {primitiveUtils.string.capitalize(value)}
      </Text>
    </>
  );

  const commuteContent = () => {
    let commuteType = walkingTime;
    let commuteIcon = Walking;

    if (showDriveTime) {
      commuteType = drivingTime;
      commuteIcon = Driving;
    }

    return (
      <>
        <SVG
          SVGElement={commuteIcon}
          height={20}
          width={20}
          fill={theme["--ll-color-on-background"]}
        />
        <Text variant="p" noMargin>
          {commuteType}
        </Text>
      </>
    );
  };

  return (
    <Popover.Content
      className={styles.content}
      side={side}
      collisionPadding={collisionPadding}
    >
      <Text variant="h5" noMargin weight="--ll-font-weight-bold">
        {poi.name}
      </Text>
      <Spacer vertical spacing="--ll-spacing-xx-small" />
      {poi.address && (
        <Text variant="p" noMargin>
          {poi.address}
        </Text>
      )}
      <Spacer vertical spacing="--ll-spacing-xx-small" />
      {poi.url && (
        <>
          <Link lineClamp={2} href={poi.url}>
            {poi.url}
          </Link>
          <Spacer vertical spacing="--ll-spacing-xx-small" />
        </>
      )}
      {typeguardUtils.poi.isSchool(poi, poiType) ? (
        <div className={styles.schoolTextContainer}>
          {poi.school_attributes?.grades &&
            SchoolText(translations.schoolLevels, poi.school_attributes.grades)}
          {poi.school_attributes?.languages &&
            SchoolText(
              translations.schoolLanguages,
              mapLanguages(poi.school_attributes.languages).join(", "),
            )}
          {poi.school_attributes?.types &&
            SchoolText(
              translations.schoolType,
              poi.school_attributes.types.join(", "),
            )}
          {poi.school_board_attributes?.name &&
            poi.school_board_attributes?.url && (
              <>
                <Text>{translations.schoolBoard}: </Text>
                <Link href={poi.school_board_attributes.url}>
                  {primitiveUtils.string.capitalize(
                    poi.school_board_attributes.name,
                  )}
                </Link>
              </>
            )}
        </div>
      ) : (
        poi.routes && (
          <div className={styles.routeContainer}>
            <SVG
              SVGElement={poiIcons[type]}
              height={20}
              width={20}
              fill={theme["--ll-color-on-background"]}
            />
            {poi.routes?.map((route, i2) => (
              <Tag
                className={styles.tag}
                key={route.short_name + i2}
                text={route.short_name}
                backgroundColor={route.route_color}
                textColor={route.text_color}
              />
            ))}
          </div>
        )
      )}
      <Tag
        text={translations.poiGroups[type]}
        size="medium"
        backgroundColor={theme["--ll-color-primary"]}
        textColor={theme["--ll-color-on-primary"]}
      />

      {calculateRoute && (
        <>
          <Spacer vertical spacing="--ll-spacing-small" />
          <div
            className={styles.routeContainer}
            data-justify={walkingTime ? "space-between" : "end"}
          >
            {walkingTime && (
              <div className={styles.routeContainer}>{commuteContent()}</div>
            )}
            <Button size="small" variant="contained" onClick={calculateRoute}>
              {translations.schoolCalculate}
            </Button>
          </div>
        </>
      )}
    </Popover.Content>
  );
};
